/* Sign-in gate for add to cart and add to wishlist.

   Mode is a theme setting (Theme settings → Customer sign-in), handed over by
   snippets/auth-gate.liquid:

   off     Nothing is gated.

   email   Shopify's own sign-in (new customer accounts: one-time code by email).
           The popup explains, remembers what the guest was doing, and sends
           them to /customer_authentication/login?return_to=<this page>. When
           they come back signed in, the remembered action is finished here.

   mobile  One-time code to the shopper's phone, entirely inside the popup: no
           page change. The code is sent through the Carat & Kin Pricing app's
           App Proxy endpoints (/apps/cart-kin/otp/...), which use whichever
           service the merchant chose (MSG91 SMS, WhatsApp, or Firebase — for
           Firebase, Google sends and checks the code in the browser and the
           app checks Google's signed token). A verified number is remembered
           for 30 days in this browser and added to the cart as
           "Verified Mobile", so it reaches the order.

   Callers ask before acting and pass a way to try again:
     if (window.CaratKinAuthGate?.intercept({ kind: "cart", items }, () => btn.click())) return;
   intercept() returns true when it has taken over. In mobile mode the retry runs
   the moment the number is verified; in email mode the pending description is
   replayed after the sign-in round trip.

   This is a storefront rule, not a security boundary: Shopify's cart API is
   public by design. */

(() => {
  if (window.CaratKinAuthGate) return;

  const PENDING_KEY = "carat_kin_auth_pending";
  const PENDING_TTL = 30 * 60 * 1000;
  const VERIFIED_KEY = "carat_kin_verified_mobile";
  const WISHLIST_KEY = "carat_kin_wishlist";
  const HANDLES_KEY = "carat_kin_wishlist_handles";
  const FIREBASE_VERSION = "10.12.2";
  const root = () => window.Shopify?.routes?.root || "/";

  let config = { mode: "off", loggedIn: false, loginPath: "/customer_authentication/login", provider: "", firebase: null, cartMobile: "" };
  try {
    const node = document.getElementById("ck-auth-gate");
    if (node) config = { ...config, ...JSON.parse(node.textContent) };
  } catch {
    /* keep defaults: gate off */
  }

  /* ---- storage helpers ---------------------------------------------------- */

  const storage = {
    get(key) {
      try {
        return JSON.parse(window.localStorage.getItem(key) || "null");
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch {
        /* private mode: memory fallbacks below keep the session working */
      }
    },
    remove(key) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    },
  };

  /* ---- who needs to sign in ----------------------------------------------- */

  // Kept in memory too, so a browser that refuses localStorage cannot end up in
  // a verify → retry → verify loop.
  let sessionVerified = null;

  function verifiedMobile() {
    const saved = sessionVerified || storage.get(VERIFIED_KEY);
    return saved && saved.token && Number(saved.expiresAt) > Date.now() ? saved : null;
  }

  function needsSignIn() {
    if (config.mode === "email") return !config.loggedIn;
    if (config.mode === "mobile") return !verifiedMobile();
    return false;
  }

  /* ---- finishing the action ------------------------------------------------ */

  function addToWishlist(id, handle) {
    const api = window.CaratKinWishlist;
    if (api) {
      if (!api.has(id)) api.toggle(id, handle);
      api.paint?.();
      return;
    }
    // wishlist.js is not on this page: write the same keys it owns and announce
    // the change the way it does, so the header badge follows.
    const ids = (storage.get(WISHLIST_KEY) || []).map(String);
    if (ids.includes(id)) return;
    ids.push(id);
    storage.set(WISHLIST_KEY, ids);
    if (handle) storage.set(HANDLES_KEY, { ...(storage.get(HANDLES_KEY) || {}), [id]: String(handle) });
    document.dispatchEvent(new CustomEvent("carat-kin:wishlist-change", { detail: { ids } }));
  }

  /** Complete a remembered action (after the email round trip, or with no retry). */
  async function finish(pending) {
    if (!pending) return;
    if (pending.kind === "cart" && Array.isArray(pending.items) && pending.items.length) {
      try {
        const response = await fetch(`${root()}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: pending.items }),
        });
        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
        await window.CaratKinCartDrawer?.refresh();
        window.CaratKinCartDrawer?.open();
      } catch (error) {
        console.error("[auth-gate] could not finish add to cart", error);
      }
      return;
    }
    if (pending.kind === "wishlist" && pending.id) {
      addToWishlist(String(pending.id), pending.handle);
      return;
    }
    document.dispatchEvent(new CustomEvent("carat-kin:auth-resume", { detail: pending }));
  }

  /* ---- email mode: pending action across the sign-in round trip ------------ */

  function savePending(pending) {
    storage.set(PENDING_KEY, { ...pending, path: window.location.pathname, at: Date.now() });
  }

  function takePending() {
    const pending = storage.get(PENDING_KEY);
    if (!pending) return null;
    if (Date.now() - Number(pending.at || 0) > PENDING_TTL) {
      storage.remove(PENDING_KEY);
      return null;
    }
    if (pending.path !== window.location.pathname) return null;
    storage.remove(PENDING_KEY);
    return pending;
  }

  function goToSignIn(pending) {
    if (pending) savePending(pending);
    const returnTo = window.location.pathname + window.location.search;
    window.location.assign(`${config.loginPath}?return_to=${encodeURIComponent(returnTo)}`);
  }

  /* ---- popup shell ----------------------------------------------------------- */

  const modal = () => document.querySelector("[data-auth-gate]");
  let lastFocus = null;
  let current = { pending: null, retry: null };

  function show(focusEl) {
    const el = modal();
    lastFocus = document.activeElement;
    el.hidden = false;
    document.body.classList.add("auth-gate-open");
    focusEl?.focus();
  }

  function close() {
    const el = modal();
    if (!el || el.hidden) return;
    el.hidden = true;
    document.body.classList.remove("auth-gate-open");
    current = { pending: null, retry: null };
    stopCountdown();
    if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
  }

  document.addEventListener("click", (event) => {
    const el = modal();
    if (!el || el.hidden) return;
    const go = event.target.closest("[data-auth-gate-continue]");
    if (go) {
      go.disabled = true;
      goToSignIn(current.pending);
      return;
    }
    if (event.target.closest("[data-auth-gate-cancel]") || event.target === el) close();
  });

  document.addEventListener("keydown", (event) => {
    const el = modal();
    if (!el || el.hidden) return;
    if (event.key === "Escape") {
      close();
      return;
    }
    if (event.key !== "Tab") return;
    // Keep focus inside the popup while it is open.
    const focusable = [...el.querySelectorAll("button:not([disabled]), input:not([disabled])")]
      .filter((node) => !node.closest("[hidden]"));
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---- mobile mode --------------------------------------------------------- */

  const step = (name) => modal()?.querySelector(`[data-otp-step="${name}"]`);
  let challenge = null; // server-signed challenge (MSG91 / WhatsApp)
  let confirmation = null; // Firebase confirmation result
  let pendingPhone = "";
  let countdown = null;

  const normalize = (value) => {
    let digits = String(value || "").replace(/\D/g, "");
    if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? digits : null;
  };
  const pretty = (phone10) => `+91 ${phone10.slice(0, 5)} ${phone10.slice(5)}`;

  function setError(form, text) {
    const node = form?.querySelector("[data-otp-error]");
    if (node) node.textContent = text || "";
  }

  function busy(button, on) {
    if (!button) return;
    if (on) {
      button.dataset.label = button.dataset.label || button.textContent;
      button.textContent = "Please wait…";
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
    button.disabled = on;
  }

  function goToStep(name) {
    const phoneStep = step("phone");
    const codeStep = step("code");
    phoneStep.hidden = name !== "phone";
    codeStep.hidden = name !== "code";
    modal().setAttribute("aria-labelledby", name === "code" ? "auth-gate-code-title" : "auth-gate-title");
    setError(phoneStep, "");
    setError(codeStep, "");
    const focusEl = name === "code" ? codeStep.querySelector("[data-otp-code]") : phoneStep.querySelector("[data-otp-phone]");
    focusEl?.focus();
  }

  function stopCountdown() {
    if (countdown) window.clearInterval(countdown);
    countdown = null;
  }

  function startCountdown(seconds) {
    const resend = step("code")?.querySelector("[data-otp-resend]");
    if (!resend) return;
    stopCountdown();
    let left = Math.max(0, Number(seconds) || 30);
    const paint = () => {
      resend.disabled = left > 0;
      resend.textContent = left > 0 ? `Resend code in ${left}s` : "Resend code";
    };
    paint();
    countdown = window.setInterval(() => {
      left -= 1;
      paint();
      if (left <= 0) stopCountdown();
    }, 1000);
  }

  async function proxy(path, body) {
    const response = await fetch(`${root()}apps/cart-kin/otp/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.message || "Something went wrong. Please try again.");
      error.code = data.error;
      error.retryAfter = data.retryAfter;
      error.attemptsLeft = data.attemptsLeft;
      throw error;
    }
    return data;
  }

  /* Firebase: loaded only when it is the active service and the popup needs it. */
  let firebase = null;
  let recaptcha = null;

  async function firebaseAuth() {
    if (firebase) return firebase;
    if (!config.firebase?.apiKey) throw new Error("Mobile sign-in is not set up yet.");
    const base = `https://www.gstatic.com/firebasejs/${FIREBASE_VERSION}`;
    const [{ initializeApp }, auth] = await Promise.all([
      import(`${base}/firebase-app.js`),
      import(`${base}/firebase-auth.js`),
    ]);
    const app = initializeApp(config.firebase, "carat-kin-auth");
    firebase = { auth: auth.getAuth(app), sdk: auth };
    return firebase;
  }

  const FIREBASE_MESSAGES = {
    "auth/invalid-phone-number": "Enter a valid 10-digit Indian mobile number.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "auth/quota-exceeded": "Verification is busy. Please try again in a few minutes.",
    "auth/invalid-verification-code": "That code is not right.",
    "auth/code-expired": "That code has expired. Request a new one.",
    "auth/captcha-check-failed": "Security check failed. Please try again.",
    "auth/unauthorized-domain": "Mobile sign-in is not enabled for this website yet.",
    "auth/operation-not-allowed": "Mobile sign-in is not enabled yet.",
  };

  function firebaseError(error) {
    const wrapped = new Error(FIREBASE_MESSAGES[error?.code] || "Something went wrong. Please try again.");
    wrapped.code = error?.code;
    return wrapped;
  }

  async function sendCode(phone10) {
    if (config.provider === "firebase") {
      const { auth, sdk } = await firebaseAuth();
      try {
        if (!recaptcha) {
          recaptcha = new sdk.RecaptchaVerifier(auth, step("phone").querySelector("[data-otp-recaptcha]"), { size: "invisible" });
        }
        confirmation = await sdk.signInWithPhoneNumber(auth, `+91${phone10}`, recaptcha);
      } catch (error) {
        try {
          recaptcha?.clear();
        } catch {
          /* ignore */
        }
        recaptcha = null;
        throw firebaseError(error);
      }
      return { resendIn: 30 };
    }
    const data = await proxy("send", { phone: phone10 });
    challenge = data.challenge;
    return data;
  }

  async function checkCode(code) {
    if (config.provider === "firebase") {
      if (!confirmation) throw new Error("Request a new code.");
      let idToken;
      try {
        const credential = await confirmation.confirm(code);
        idToken = await credential.user.getIdToken();
      } catch (error) {
        throw firebaseError(error);
      }
      const data = await proxy("firebase", { idToken });
      // The storefront keeps our own token; Firebase's session is not needed.
      firebase?.sdk.signOut(firebase.auth).catch(() => {});
      return data;
    }
    return proxy("verify", { challenge, code });
  }

  function rememberVerified(data) {
    const record = { token: data.token, phone: data.phone, expiresAt: data.expiresAt };
    sessionVerified = record;
    storage.set(VERIFIED_KEY, record);
    tagCart(data.phone);
  }

  /** Put the verified number on the cart so it reaches the order. */
  function tagCart(phone) {
    if (!phone || config.cartMobile === phone) return;
    config.cartMobile = phone;
    fetch(`${root()}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { "Verified Mobile": phone } }),
    }).catch(() => {});
  }

  function openMobile(pending, retry) {
    const el = modal();
    if (!el) return;
    current = { pending: pending || null, retry: retry || null };
    challenge = null;
    confirmation = null;
    const phoneInput = step("phone").querySelector("[data-otp-phone]");
    if (pendingPhone && !phoneInput.value) phoneInput.value = pendingPhone;
    step("code").querySelector("[data-otp-code]").value = "";
    show(phoneInput);
    goToStep("phone");
    if (!config.provider) setError(step("phone"), "Mobile sign-in is not set up yet. Please try again later.");
  }

  async function onSend(form, isResend = false) {
    const phone = normalize(step("phone").querySelector("[data-otp-phone]").value);
    if (!phone) {
      setError(step("phone"), "Enter a valid 10-digit Indian mobile number.");
      goToStep("phone");
      return;
    }
    const button = isResend ? step("code").querySelector("[data-otp-resend]") : form.querySelector("[data-otp-send]");
    if (isResend) {
      // The countdown owns this button's text; just lock it while sending.
      button.disabled = true;
      button.textContent = "Sending…";
    } else {
      busy(button, true);
    }
    setError(form, "");
    try {
      const data = await sendCode(phone);
      pendingPhone = phone;
      step("code").querySelector("[data-otp-target]").textContent = pretty(phone);
      step("code").querySelector("[data-otp-code]").value = "";
      if (!isResend) goToStep("code");
      startCountdown(data.resendIn || 30);
    } catch (error) {
      setError(form, error.message);
      if (isResend) startCountdown(error.retryAfter || 0);
    } finally {
      if (!isResend) busy(button, false);
    }
  }

  let verifying = false;

  async function onVerify(form) {
    // Six digits auto-submit, so a tap on Verify can race it; check once.
    if (verifying) return;
    const input = form.querySelector("[data-otp-code]");
    const code = input.value.replace(/\D/g, "");
    if (code.length !== 6) {
      setError(form, "Enter the 6-digit code.");
      input.focus();
      return;
    }
    const button = form.querySelector("[data-otp-verify]");
    verifying = true;
    busy(button, true);
    setError(form, "");
    try {
      const data = await checkCode(code);
      rememberVerified(data);
      const { pending, retry } = current;
      close();
      if (typeof retry === "function") retry();
      else finish(pending);
    } catch (error) {
      let message = error.message;
      if (error.code === "wrong-code" && typeof error.attemptsLeft === "number") {
        message = `That code is not right. ${error.attemptsLeft} attempt${error.attemptsLeft === 1 ? "" : "s"} left.`;
      }
      setError(form, message);
      input.select();
    } finally {
      verifying = false;
      busy(button, false);
    }
  }

  if (config.mode === "mobile") {
    document.addEventListener("submit", (event) => {
      const form = event.target.closest?.("[data-otp-step]");
      if (!form || !modal()?.contains(form)) return;
      event.preventDefault();
      if (form.dataset.otpStep === "phone") onSend(form);
      else onVerify(form);
    });

    document.addEventListener("click", (event) => {
      if (!modal() || modal().hidden) return;
      if (event.target.closest("[data-otp-change]")) {
        stopCountdown();
        goToStep("phone");
      } else if (event.target.closest("[data-otp-resend]")) {
        onSend(step("code"), true);
      }
    });

    // Submit the code as soon as six digits are in (typed, pasted or autofilled).
    document.addEventListener("input", (event) => {
      const input = event.target.closest?.("[data-otp-code]");
      if (!input || !modal()?.contains(input)) return;
      input.value = input.value.replace(/\D/g, "").slice(0, 6);
      if (input.value.length === 6) input.form?.requestSubmit?.();
    });

    // Someone verified in an earlier visit: make sure this cart carries it.
    const known = verifiedMobile();
    if (known) tagCart(known.phone);
  }

  /* ---- public API ------------------------------------------------------------ */

  /** Returns true when the action was stopped because the shopper must sign in. */
  function intercept(pending, retry) {
    if (!needsSignIn()) return false;
    if (config.mode === "mobile") {
      openMobile(pending, retry);
      return true;
    }
    // Email mode.
    const el = modal();
    if (!el) {
      goToSignIn(pending);
      return true;
    }
    current = { pending: pending || null, retry: null };
    const button = el.querySelector("[data-auth-gate-continue]");
    if (button) button.disabled = false;
    show(button);
    return true;
  }

  window.CaratKinAuthGate = { intercept, needsSignIn, close, verifiedMobile };

  // Email mode: back from Shopify's sign-in, finish what the guest started.
  // Loaded deferred at the end of the layout, so the cart drawer, wishlist and
  // section scripts it hands off to have already run.
  if (config.mode === "email" && config.loggedIn) finish(takePending());
})();
